// The store's side of a request (batch 4, lane store):
//   ApproveSendBlock — a sent request: "Approve & send what we have" in one
//     step (POST …/approve-and-issue, one transaction, one Idempotency-Key).
//   SendRestBlock    — an approved or part-sent request: send the rest, or
//     mark the rest of a line as not available.
// Every quantity shows its unit; Send now is pre-filled with what the store
// has in date (Batch 1's usable quantity), never expired stock.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn, formatDateHuman } from '@/lib/utils'
import { fmtQty } from '@/lib/quantity'
import { T } from '@/lib/inventoryFlow/strings'
import { ST } from '@/lib/inventoryFlow/storeScreenStrings'
import { expiredWarning, issuePrefill, storeUsable } from '@/lib/inventoryFlow/drafts'
import {
  approveAndIssueBody,
  approveSendLabel,
  expiredWords,
  sendNowError,
  sendPrefill,
  sendRestError,
  sendSummary,
  shortList,
  shortWords,
  visibleSendRows,
} from '@/lib/inventoryFlow/storeScreenLogic'
import { intentScope, type IntentKeys } from '@/lib/inventoryFlow/intentKey'
import type { Requisition, RequisitionLine } from '@/lib/inventoryFlow/types'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { IM } from '@/components/store/inventory/itemMasterStrings'
import { ScanTally } from '@/lib/scanToLine'
import { BuyShortfallButton } from '@/components/store/purchasing/BuyShortfallButton'

type Notify = (kind: 'success' | 'error', message: string) => void

const EPS = 1e-6

function errMessage(e: unknown): string {
  return friendlyError(e)
}

/** A number box with its unit beside it. Shows what is typed while typing. */
function SendInput({ value, unit, label, onChange, invalid }: { value: number; unit: string; label: string; onChange: (n: number) => void; invalid?: boolean }) {
  const [text, setText] = useState<string | null>(null)
  return (
    <label className="relative inline-flex items-center justify-end gap-1.5">
      <span className="sr-only">{label}</span>
      <Input
        type="number"
        inputMode="decimal"
        min={0}
        className={cn('w-20 text-right tabular-nums', invalid && 'border-destructive')}
        value={text ?? (Number.isFinite(value) ? String(value) : '')}
        onChange={(e) => {
          setText(e.target.value)
          const n = parseFloat(e.target.value)
          onChange(e.target.value.trim() === '' ? 0 : Number.isFinite(n) ? n : Number.NaN)
        }}
        onBlur={() => setText(null)}
      />
      <span className="min-w-[2rem] text-left text-sm text-muted-foreground">{unit}</span>
    </label>
  )
}

/** Scan each pack as it is handed over (Lane A scanner): the first scan of a
 *  line replaces the pre-fill. Fast scans: the tally keeps the latest count
 *  between renders, so two codes in a row never count from the same number. */
function useSendScanner(lines: RequisitionLine[], qty: Record<string, number>, setQty: (f: (s: Record<string, number>) => Record<string, number>) => void, resetKey: string) {
  const tally = useRef<ScanTally | null>(null)
  if (!tally.current) tally.current = new ScanTally()
  tally.current.sync(qty)
  useEffect(() => { tally.current?.reset() }, [resetKey])
  return useItemScanner({
    enabled: lines.length > 0,
    onItem: (match) => {
      const out = tally.current!.apply(lines, (l) => l.stock_item_id, (l) => l.id, match)
      if (out.kind === 'not_on_list') return { ok: false, text: IM.scan.notOnList(match.item.name, 'request') }
      setQty((s) => ({ ...s, [out.line.id]: out.qty }))
      return { ok: true, text: IM.scan.added(out.line.item_name, fmtQty(out.qty), out.line.unit) }
    },
  })
}

function ItemCell({ l, onThrowAway, children }: { l: RequisitionLine; onThrowAway?: () => void; children?: React.ReactNode }) {
  const expired = expiredWords(l)
  return (
    <td className="p-2.5 align-top">
      <div className="font-medium">{l.item_name}</div>
      {children}
      {expired && (
        <div className="mt-0.5 text-xs text-red-700 dark:text-red-300">
          {expired}{' '}
          {onThrowAway && (
            <button type="button" onClick={onThrowAway} className="font-medium underline underline-offset-2">{ST.send.throwAway}</button>
          )}
        </div>
      )}
    </td>
  )
}

export function ApproveSendBlock({ r, keys, notify, onDone, onThrowAway }: {
  r: Requisition
  keys: IntentKeys
  notify: Notify
  onDone: () => void
  /** Opens where expired stock is written off (Alerts). */
  onThrowAway?: () => void
}) {
  const lines = useMemo(() => r.lines ?? [], [r.lines])
  const [qty, setQty] = useState<Record<string, number>>({})
  const [expanded, setExpanded] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [rejectWhy, setRejectWhy] = useState('')
  useEffect(() => {
    setQty(Object.fromEntries(lines.map((l) => [l.id, sendPrefill(l)])))
  }, [r.id, r.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps
  const scanner = useSendScanner(lines, qty, setQty, `${r.id}|${r.updated_at}`)

  const summary = sendSummary(lines, qty, (l) => l.requested_qty)
  const errors = lines.map((l) => sendNowError(l, qty[l.id] ?? 0))
  const firstError = errors.find(Boolean)
  const needsLook = (l: RequisitionLine) =>
    !!shortWords(l) || !!expiredWords(l) || Math.abs((qty[l.id] ?? 0) - sendPrefill(l)) > EPS
  const { shown, hidden } = visibleSendRows(lines, needsLook, expanded)

  // The shortfall (what to buy) nets out stock already ordered for this
  // request; fetched fresh so the button never offers to buy it twice.
  const { data: sf } = useQuery({
    queryKey: ['inventory-flow', 'shortfall', r.id],
    queryFn: () => apiClient.getRequisitionShortfall(r.id),
    staleTime: 0,
    enabled: summary.short.length > 0,
  })
  const canBuy = (sf?.data?.suppliers ?? []).some((g) => g.supplier_id && g.lines.some((l) => l.shortfall_qty > EPS))

  const scope = intentScope('approve-and-issue', r.id, r.updated_at)
  const rejectScope = intentScope('reject', r.id, r.updated_at)
  const approveSend = useMutation({
    mutationFn: () => apiClient.requisitionAction(r.id, 'approve-and-issue', approveAndIssueBody(lines, qty), keys.keyFor(scope)),
    onSuccess: () => {
      keys.renew(scope)
      notify('success', summary.sending > 0 ? ST.send.approveSendOk : ST.send.approveOnlyOk)
      onDone()
    },
    onError: (e) => { notify('error', errMessage(e)); onDone() },
  })
  const reject = useMutation({
    mutationFn: () => apiClient.requisitionAction(r.id, 'reject', { reason: rejectWhy }, keys.keyFor(rejectScope)),
    onSuccess: () => { keys.renew(rejectScope); notify('success', ST.send.sendBackOk); onDone() },
    onError: (e) => { notify('error', errMessage(e)); onDone() },
  })

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold">{ST.send.title}</h3>
      {scanner.ui}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[30rem] text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-2.5 font-medium">{ST.send.item}</th>
              <th className="p-2.5 text-right font-medium">{ST.send.asked}</th>
              <th className="p-2.5 text-right font-medium">{ST.send.storeHas}</th>
              <th className="p-2.5 text-right font-medium">{ST.send.sendNow}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => {
              const short = shortWords(l)
              const has = Math.max(storeUsable(l), 0)
              const err = errors[lines.indexOf(l)]
              return (
                <tr key={l.id} className={cn('border-t', expiredWords(l) && has <= EPS ? 'bg-red-50/60 dark:bg-red-950/20' : short && 'bg-amber-50/70 dark:bg-amber-950/20')}>
                  <ItemCell l={l} onThrowAway={onThrowAway}>
                    {short && <div className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">{short}</div>}
                    {l.fefo_expiry && has > EPS && <div className="mt-0.5 text-xs text-muted-foreground">{T.useFirst(formatDateHuman(l.fefo_expiry))}</div>}
                    {err && <div className="mt-0.5 text-xs text-destructive">{err}</div>}
                  </ItemCell>
                  <td className="whitespace-nowrap p-2.5 text-right align-top tabular-nums">{fmtQty(l.requested_qty)} {l.unit}</td>
                  <td className="whitespace-nowrap p-2.5 text-right align-top tabular-nums">{fmtQty(has)} {l.unit}</td>
                  <td className="p-2.5 text-right align-top">
                    {has <= EPS && (qty[l.id] ?? 0) <= EPS ? (
                      <span className="whitespace-nowrap text-muted-foreground">{ST.send.nothingToSend}</span>
                    ) : (
                      <SendInput value={qty[l.id] ?? 0} unit={l.unit} label={ST.send.sendLabel(l.item_name)} invalid={!!err}
                        onChange={(n) => setQty((s) => ({ ...s, [l.id]: n }))} />
                    )}
                  </td>
                </tr>
              )
            })}
            {hidden > 0 && (
              <tr className="border-t">
                <td colSpan={4} className="p-2.5">
                  <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={() => setExpanded(true)}>
                    {ST.send.moreInStock(hidden)}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {summary.short.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3 text-sm">
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{ST.send.short(shortList(summary.short))}</div>
            <div className="text-muted-foreground">{ST.send.shortHelp}</div>
          </div>
          {canBuy && <BuyShortfallButton requisitionId={r.id} showToast={notify} />}
        </div>
      )}

      {rejecting ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input className="min-w-[14rem] flex-1" placeholder={ST.send.sendBackReason} value={rejectWhy} onChange={(e) => setRejectWhy(e.target.value)} autoFocus />
          <Button variant="destructive" disabled={!rejectWhy.trim() || reject.isPending} onClick={() => reject.mutate()}>{ST.send.sendBack}</Button>
          <Button variant="ghost" onClick={() => { setRejecting(false); setRejectWhy('') }}>{ST.send.cancel}</Button>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => setRejecting(true)}>{ST.send.sendBack}</Button>
          <Button disabled={!!firstError || approveSend.isPending} onClick={() => approveSend.mutate()}>
            {approveSend.isPending ? T.sending : approveSendLabel(summary)}
          </Button>
        </div>
      )}
    </section>
  )
}

export function SendRestBlock({ r, keys, notify, onDone }: { r: Requisition; keys: IntentKeys; notify: Notify; onDone: () => void }) {
  const lines = useMemo(() => (r.lines ?? []).filter((l) => l.to_issue_qty > EPS), [r.lines])
  const [qty, setQty] = useState<Record<string, number>>({})
  const [shortFor, setShortFor] = useState<string | null>(null)
  const [shortWhy, setShortWhy] = useState('')
  useEffect(() => {
    setQty(Object.fromEntries(lines.map((l) => [l.id, issuePrefill(l)])))
  }, [r.id, r.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps
  const scanner = useSendScanner(lines, qty, setQty, `${r.id}|${r.updated_at}`)

  const errors = lines.map((l) => sendRestError(l, qty[l.id] ?? 0))
  const sending = lines.filter((l) => (qty[l.id] ?? 0) > EPS).length
  const issueScope = intentScope('issue', r.id, r.updated_at)
  const shortScope = (lineId: string) => intentScope('close-short', r.id, lineId, r.updated_at)
  const issue = useMutation({
    mutationFn: () =>
      apiClient.requisitionAction(r.id, 'issue', {
        lines: lines.filter((l) => (qty[l.id] ?? 0) > 0).map((l) => ({ line_id: l.id, qty: qty[l.id] })),
      }, keys.keyFor(issueScope)),
    onSuccess: () => { keys.renew(issueScope); notify('success', T.issueOk); onDone() },
    onError: (e) => { notify('error', errMessage(e)); onDone() },
  })
  const closeShort = useMutation({
    mutationFn: (lineId: string) => apiClient.requisitionLineAction(r.id, lineId, 'close-short', { reason: shortWhy }, keys.keyFor(shortScope(lineId))),
    onSuccess: (_res, lineId) => { keys.renew(shortScope(lineId)); setShortFor(null); setShortWhy(''); notify('success', T.saved); onDone() },
    onError: (e) => { notify('error', errMessage(e)); onDone() },
  })

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold">{r.status === 'partially_issued' ? ST.send.restTitle : T.issue}</h3>
      {scanner.ui}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[30rem] text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-2.5 font-medium">{ST.send.item}</th>
              <th className="p-2.5 text-right font-medium">{ST.send.stillToSend}</th>
              <th className="p-2.5 text-right font-medium">{ST.send.storeHas}</th>
              <th className="p-2.5 text-right font-medium">{ST.send.sendNow}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const has = Math.max(storeUsable(l), 0)
              const err = errors[i]
              const warn = expiredWarning(l)
              return (
                <tr key={l.id} className={cn('border-t', has + EPS < l.to_issue_qty && 'bg-amber-50/70 dark:bg-amber-950/20')}>
                  <td className="p-2.5 align-top">
                    <div className="font-medium">{l.item_name}</div>
                    {l.fefo_expiry && has > EPS && <div className="mt-0.5 text-xs text-muted-foreground">{T.useFirst(formatDateHuman(l.fefo_expiry))}</div>}
                    {warn && <div className="mt-0.5 text-xs text-red-700 dark:text-red-300">{warn}</div>}
                    {err && <div className="mt-0.5 text-xs text-destructive">{err}</div>}
                    {shortFor === l.id ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Input className="min-w-[12rem] flex-1" placeholder={ST.send.notAvailableReason} value={shortWhy} onChange={(e) => setShortWhy(e.target.value)} autoFocus />
                        <Button size="sm" variant="destructive" disabled={!shortWhy.trim() || closeShort.isPending} onClick={() => closeShort.mutate(l.id)}>{ST.send.notAvailableSave}</Button>
                        <Button size="sm" variant="ghost" onClick={() => setShortFor(null)}>{ST.send.cancel}</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="mt-2" onClick={() => { setShortFor(l.id); setShortWhy('') }}>{ST.send.markNotAvailable}</Button>
                    )}
                  </td>
                  <td className="whitespace-nowrap p-2.5 text-right align-top tabular-nums">{fmtQty(l.to_issue_qty)} {l.unit}</td>
                  <td className="whitespace-nowrap p-2.5 text-right align-top tabular-nums">{fmtQty(has)} {l.unit}</td>
                  <td className="p-2.5 text-right align-top">
                    {has <= EPS && (qty[l.id] ?? 0) <= EPS ? (
                      <span className="whitespace-nowrap text-muted-foreground">{ST.send.nothingToSend}</span>
                    ) : (
                      <SendInput value={qty[l.id] ?? 0} unit={l.unit} label={ST.send.sendLabel(l.item_name)} invalid={!!err}
                        onChange={(n) => setQty((s) => ({ ...s, [l.id]: n }))} />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <Button disabled={errors.some(Boolean) || sending === 0 || issue.isPending} onClick={() => issue.mutate()}>
          {issue.isPending ? T.sending : sending === 0 ? ST.send.nothingYet : ST.send.sendRest(sending)}
        </Button>
      </div>
    </section>
  )
}
